import React, { useState, useEffect, useCallback } from 'react';
import { t } from '../i18n';
import { salesAPI } from '../api';
import toast from 'react-hot-toast';

// ─── Бланк менеджера: заказы декорами и матрицами ────────────────────────────
//
// Менеджер держит принятые заказы на бумаге и переписывал их от руки: декоры
// строками, матрицы столбцами, в клетке — число штук. Лист лежит на столе всю
// неделю, по нему звонят клиенту и по нему же отмечают, что цех уже выдал.
//
// Здесь тот же лист, только заполненный системой. В клетке ОДНО число:
// вышло из цеха — стоит выпущенное, не вышло — стоит заказанное. Какое из
// двух, говорит вид числа: обведено кружком — выпущено, курсивом и бледнее —
// ещё нет, зачёркнуто — заказ отменили.
//
// Кружок оставлен потому, что им же эти числа отмечали ручкой: человек
// читает лист, не переучиваясь.

const CARD = {
  background: 'var(--card)', border: '1px solid var(--border)',
  borderRadius: 12, padding: '12px 14px',
};
const INPUT = {
  background: 'var(--input)', border: '1px solid var(--border)',
  borderRadius: 8, color: 'var(--text)', padding: '7px 10px',
  fontSize: 13, outline: 'none', fontFamily: 'inherit',
};
const chip = (on) => ({
  padding: '6px 13px', borderRadius: 20, fontSize: 12.5, fontWeight: 600,
  cursor: 'pointer', fontFamily: 'inherit',
  background: on ? 'var(--accent)' : 'var(--bg)',
  border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)'),
  color: on ? '#fff' : 'var(--muted)',
});
const BTN = {
  background: 'var(--accent)', border: 'none', borderRadius: 9, color: '#fff',
  padding: '9px 18px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
  fontFamily: 'inherit', whiteSpace: 'nowrap',
};
const GHOST = { ...BTN, background: 'transparent', color: 'var(--muted)',
                border: '1px solid var(--border)' };

// YYYY-MM-DD в местном времени: toISOString() здесь не подходит — около
// полуночи он может вернуть соседний день из-за перевода даты в UTC.
function localDateValue(dayOffset = 0) {
  const date = new Date();
  date.setDate(date.getDate() + dayOffset);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Клетка листа. Число ОДНО: вышло из цеха — стоит выпущенное, не вышло —
// стоит заказанное. Двух чисел рядом («12 из 40») на бумаге не нужно: лист
// читают на ходу, и глаз должен брать цифру, а не разбирать дробь.
//
// Какое это из двух, говорит вид числа, а не подпись: обведено — выпущено,
// курсивом — ещё нет. Зачёркнуто — заказ отменили, делать по нему нечего.
function Cell({ plan, fact, cancelled }) {
  if (!plan && !fact) return null;
  const out = fact > 0;
  const strike = cancelled ? { textDecoration: 'line-through' } : {};
  if (out) return (
    <span style={{
      display: 'inline-block', minWidth: 17, height: 17, lineHeight: '17px',
      padding: '0 3px', borderRadius: 12, border: '1.2px solid #000',
      fontWeight: 700, fontSize: 9.5, color: '#000', textAlign: 'center',
      ...strike,
    }}>{fact}</span>
  );
  return (
    <span style={{ fontSize: 9.5, fontStyle: 'italic', color: '#666', ...strike }}>
      {plan}
    </span>
  );
}

// Ширина столбца заказчика: имена бывают и «БМТ», и в три слова. Считаем по
// самому длинному, но в разумных пределах — иначе он съест матрицы.
function clientWidth(rows) {
  const max = rows.reduce((m, r) => Math.max(m, (r.client || '').length), 0);
  return Math.min(180, Math.max(80, max * 6));
}

function Sheet({ data, title, period }) {
  const rows = data.rows || [];
  const textures = data.textures || [];
  const TH = {
    border: '1px solid #000', padding: '3px 3px', fontSize: 8.5, fontWeight: 700,
    background: '#eee', color: '#000', textAlign: 'center', lineHeight: 1.1,
  };
  const TD = {
    border: '1px solid #000', padding: '2px 4px', fontSize: 9.5, color: '#000',
    lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden',
    textOverflow: 'ellipsis',
  };
  const NUM = { ...TD, textAlign: 'center' };
  const cw = clientWidth(rows);

  // Итог по столбцу складывает ровно те числа, что стоят в клетках: где
  // вышло — выпущенное, где нет — заказанное. Иначе итог спорил бы со
  // столбцом, из которого он сложен.
  const totals = textures.map(tx => rows
    .filter(r => r.texture === tx && r.status !== 'cancelled')
    .reduce((sum, r) => sum + (r.fact > 0 ? r.fact : (r.plan || 0)), 0));
  const grandTotal = totals.reduce((sum, value) => sum + value, 0);

  return (
    <div className="sheet" style={{
      background: '#fff', padding: 10, width: 1069, maxWidth: '100%',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#000' }}>{title}</div>
          <div style={{ fontSize: 10, color: '#000', marginTop: 2 }}>{period}</div>
        </div>
        {/* Как читать лист. Печатается вместе с ним: бумага уходит из рук, и
            объяснить кружок будет некому. */}
        <div style={{ fontSize: 8.5, color: '#000', lineHeight: 1.5, whiteSpace: 'nowrap' }}>
          <div><b style={{
            display: 'inline-block', minWidth: 17, height: 17, lineHeight: '17px',
            borderRadius: 12, border: '1.2px solid #000', textAlign: 'center',
            fontSize: 9, padding: '0 3px',
          }}>40</b> — выпущено цехом</div>
          <div><i style={{ color: '#666' }}>40</i> — заказано, ещё не выпущено</div>
          <div><span style={{ textDecoration: 'line-through', color: '#555' }}>40</span>
            {' '}— заказ отменён</div>
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th style={{ ...TH, width: 42 }}>толщина</th>
            <th style={TH}>декор</th>
            {textures.map(tx => (
              // Названия матриц длинные («мягкое дерево», «глубокая выжимка»),
              // а столбцов до десятка. Вертикально они занимают ширину буквы,
              // и лист остаётся читаемым на A4 — так же, как на бумажном.
              <th key={tx} style={{ ...TH, width: 26, height: 96, padding: 1 }}>
                <div style={{
                  writingMode: 'vertical-rl', transform: 'rotate(180deg)',
                  margin: '0 auto', whiteSpace: 'nowrap', fontSize: 8.5,
                }}>{tx}</div>
              </th>
            ))}
            <th style={{ ...TH, width: cw }}>Клиент</th>
            <th style={{ ...TH, width: 60 }}>срок</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            // Заказы одного клиента идут подряд — отделяем их линией потолще,
            // чтобы на бумаге взгляд находил, где кончается один клиент.
            const newClient = i === 0 || rows[i - 1].client !== r.client;
            const top = newClient && i > 0 ? { borderTop: '2px solid #000' } : {};
            // Отменённый заказ со стола не убирают — его вычёркивают. Строка
            // остаётся на месте, и видно, что по ней делать нечего.
            const off = r.status === 'cancelled';
            const cross = off ? { textDecoration: 'line-through', color: '#555' } : {};
            return (
              <tr key={`${r.order_id}-${i}`}>
                <td style={{ ...NUM, ...top, ...cross }}>{r.thickness ? `${r.thickness}мм` : ''}</td>
                <td style={{ ...TD, ...top, ...cross, textTransform: 'uppercase' }}>
                  {r.decor}
                  {r.is_service && (
                    <span style={{ fontSize: 8, marginLeft: 4 }}>(плита клиента)</span>
                  )}
                </td>
                {textures.map(tx => (
                  <td key={tx} style={{ ...NUM, ...top, padding: '2px 1px' }}>
                    {r.texture === tx
                      ? <Cell plan={r.plan} fact={r.fact} cancelled={off} />
                      : null}
                  </td>
                ))}
                <td style={{ ...TD, ...top, ...cross, fontWeight: 700 }}>{r.client}</td>
                <td style={{ ...NUM, ...top, ...cross, fontSize: 8.5 }}>
                  {r.deadline ? new Date(r.deadline).toLocaleDateString('ru-RU') : ''}
                </td>
              </tr>
            );
          })}
          <tr>
            <td colSpan={2} style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>
              Итого, шт
            </td>
            {totals.map((tt, i) => (
              <td key={i} style={{ ...NUM, fontWeight: 700, fontSize: 8.5, padding: '2px 1px' }}>
                {tt || ''}
              </td>
            ))}
            <td style={{ ...TD, textAlign: 'center', fontWeight: 700, fontSize: 10.5 }}>
              Всего {grandTotal} шт
            </td>
            <td style={TD}></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function OrderSheet() {
  // Лист печатают под себя — по умолчанию он свой. Общий по цеху берут,
  // когда подменяют коллегу: галочка снимается, приходит весь цех.
  const [mine, setMine] = useState(true);
  const [state, setState] = useState('open');
  // При первом открытии показываем только принятые сегодня заказы.
  const [dateFrom, setDateFrom] = useState(() => localDateValue());
  const [dateTo, setDateTo] = useState(() => localDateValue());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    salesAPI.getOrderSheet({
      mine: mine ? 1 : 0,
      state,
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo ? { date_to: dateTo } : {}),
    })
      .then(r => setData(r.data))
      .catch(() => toast.error(t('Не удалось загрузить бланк')))
      .finally(() => setLoading(false));
  }, [mine, state, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  // Печать в ОТДЕЛЬНОМ документе, а не экрана: тёмный фон приложения ушёл бы
  // в принтер, а строки, не поместившиеся на первую страницу, обрезались.
  const print = () => {
    const node = document.getElementById('order-sheet-print');
    if (!node) return;
    const doc = `<!doctype html><html><head><meta charset="utf-8"><title></title>
<style>
  @page { size: A4 landscape; margin: 7mm; }
  html, body { margin:0; padding:0; background:#fff; color:#000;
               font-family: Arial, Helvetica, sans-serif; }
  table { width:100%; border-collapse:collapse; }
  thead { display:table-header-group; }
  tr { page-break-inside:avoid; }
  .sheet { width:auto !important; max-width:none !important; padding:0 !important; }
</style></head><body>${node.innerHTML}</body></html>`;

    const frame = document.createElement('iframe');
    frame.style.cssText =
      'position:fixed;left:-10000px;top:0;width:1123px;height:794px;border:0;';
    document.body.appendChild(frame);
    const fdoc = frame.contentWindow.document;
    fdoc.open(); fdoc.write(doc); fdoc.close();
    setTimeout(() => {
      try {
        frame.contentWindow.focus();
        frame.contentWindow.print();
      } catch { toast.error(t('Не удалось открыть печать')); }
      setTimeout(() => {
        if (document.body.contains(frame)) document.body.removeChild(frame);
      }, 2000);
    }, 250);
  };

  const rows = (data && data.rows) || [];
  const today = new Date().toLocaleDateString('ru-RU');
  const period = [
    dateFrom ? `с ${new Date(dateFrom).toLocaleDateString('ru-RU')}` : '',
    dateTo ? `по ${new Date(dateTo).toLocaleDateString('ru-RU')}` : '',
    state === 'open' ? 'заказы в работе' : 'все заказы',
    `напечатано ${today}`,
  ].filter(Boolean).join(' · ');
  const title = mine
    ? `Заказы менеджера — ${(data && data.manager) || ''}`
    : 'Заказы цеха';

  const yesterdayValue = localDateValue(-1);
  const todayValue = localDateValue();
  const chooseDay = (value) => {
    setDateFrom(value);
    setDateTo(value);
  };

  return (
    <div>
      <div style={{ ...CARD, display: 'flex', gap: 10, alignItems: 'center',
                    flexWrap: 'wrap', marginBottom: 14 }}>
        {/* Чьи заказы. Раньше выбора не было вовсе — менеджер видел только
            свои, и заказ коллеги в отпуске не существовал для него нигде. */}
        <button style={chip(mine)} onClick={() => setMine(true)}>{t('Мои заказы')}</button>
        <button style={chip(!mine)} onClick={() => setMine(false)}>{t('Все по филиалу')}</button>
        <span style={{ width: 1, height: 22, background: 'var(--border)' }} />
        <button style={chip(state === 'open')} onClick={() => setState('open')}>
          {t('В работе')}
        </button>
        <button style={chip(state === 'all')} onClick={() => setState('all')}>
          {t('Все, включая отгруженные')}
        </button>
        <span style={{ width: 1, height: 22, background: 'var(--border)' }} />
        <button style={chip(dateFrom === yesterdayValue && dateTo === yesterdayValue)}
                onClick={() => chooseDay(yesterdayValue)}>
          {t('Вчера')}
        </button>
        <button style={chip(dateFrom === todayValue && dateTo === todayValue)}
                onClick={() => chooseDay(todayValue)}>
          {t('Сегодня')}
        </button>
        <label style={{ color: 'var(--muted)', fontSize: 12.5 }}>{t('приняты с')}</label>
        <input type="date" style={INPUT} value={dateFrom}
               onChange={e => setDateFrom(e.target.value)} />
        <label style={{ color: 'var(--muted)', fontSize: 12.5 }}>{t('по')}</label>
        <input type="date" style={INPUT} value={dateTo}
               onChange={e => setDateTo(e.target.value)} />
        {(dateFrom || dateTo) && (
          <button style={GHOST} onClick={() => { setDateFrom(''); setDateTo(''); }}>
            {t('Сбросить даты')}
          </button>
        )}
        <button style={GHOST} onClick={load}>↻ {t('Обновить')}</button>
        <button style={{ ...BTN, marginLeft: 'auto' }} onClick={print}
                disabled={!rows.length}>
          🖨 {t('Печать A4')}
        </button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--dim)', textAlign: 'center', padding: 30 }}>
          {t('Загрузка…')}
        </p>
      ) : !rows.length ? (
        <p style={{ color: 'var(--dim)', textAlign: 'center', padding: 30 }}>
          {t('Заказов по выбранным условиям нет')}
        </p>
      ) : (
        <div id="order-sheet-print" style={{ overflowX: 'auto' }}>
          <Sheet data={data} title={title} period={period} />
        </div>
      )}
    </div>
  );
}
